import { create } from 'zustand'
import { DEFAULT_MAX_RECORD_FILE_UPLOAD_CONCURRENCY } from '~/utils/constants'

export const fileStatuses = [
  'queue',
  'uploading',
  'err_could_not_upload',
  'classifying',
  'done',
] as const

type FileStatus = (typeof fileStatuses)[number]

export const classificationStatuses = [
  'queue',
  'starting',
  'reading_file',
  'classifying_category',
  'classifying_file_class',
  'done',
  'err_could_not_read_file',
  'err_could_not_classify_category',
  'err_could_not_classify_file_class',
  'err_unable_to_find_suggested_category',
  'err_unable_to_find_suggested_file_class',
] as const

export type ClassificationStatus = (typeof classificationStatuses)[number]

export const classificationLabels: Record<ClassificationStatus, string> = {
  queue: 'Waiting on Queue',
  starting: 'Starting Classification',
  reading_file: 'Reading File Contents',
  classifying_category: 'Classifying Record Category',
  classifying_file_class: 'Classifying File Class',
  done: 'Classification Complete',
  err_could_not_read_file: 'Error: Could Not Read File',
  err_could_not_classify_category: 'Error: Could Not Classify Category',
  err_could_not_classify_file_class: 'Error: Could Not Classify File Class',
  err_unable_to_find_suggested_category: 'Error: Unable to Find Suggested Category',
  err_unable_to_find_suggested_file_class: 'Error: Unable to Find Suggested File Class',
}

export interface QueueFile {
  id: string
  file: File
  reportId: string
  status: FileStatus
  classificationStatus: ClassificationStatus
  uploadProgress?: number
  remoteReason?: string
  recordFileId?: string
  result?: {
    category?: {
      final?: string
      suggestion?: string
    }
    fileClass?: {
      final?: string
      suggestion?: string
    }
  }
  isMoving?: boolean
  windowLocation?: string
}

interface RecordClassificationQueue {
  queue: QueueFile[]
  isQueueActive: () => boolean

  getActive: () => QueueFile[]
  getCurrentReportId: () => string | null
  getWindowLocation: () => string | null

  maxConcurrency: number
  setMaxConcurrency: (newValue: number) => void

  autoStart: boolean
  setAutoStart: (newValue: boolean) => void

  addFiles: (files: File[], reportId: string, windowLocation?: string) => void
  importPending: (queueFiles: QueueFile[]) => void
  removeFile: (id: string) => void
  removeFileByRecordFileId: (recordFileId: string) => void
  updateFileStatus: (
    id: string,
    status: FileStatus,
    data?: {
      classificationStatus?: ClassificationStatus
      remoteReason?: string
      uploadProgress?: number
      recordFileId?: string
      isMoving?: boolean
    },
  ) => void
  updateFileStatusByRecordId: (
    id: string,
    status: FileStatus,
    data?: {
      classificationStatus?: ClassificationStatus
      remoteReason?: string
      uploadProgress?: number
      recordFileId?: string
      isMoving?: boolean
      result?: QueueFile['result']
    },
  ) => void
  clearCompleted: () => void
  getFileById: (id: string) => QueueFile | undefined
}

export const useRecordClassificationQueueStore = create<RecordClassificationQueue>((set, get) => ({
  queue: [],
  isQueueActive: () => {
    return get().queue.some((qf) => ['queue', 'uploading', 'classifying'].includes(qf.status))
  },

  getWindowLocation: () => {
    return get().queue.find((i) => i.windowLocation)?.windowLocation ?? null
  },

  getActive: () => {
    return get().queue.filter((qf) => !['queue', 'uploading', 'classifying'].includes(qf.status))
  },
  getCurrentReportId: () => {
    return (
      get().queue.find((qf) => ['queue', 'uploading', 'classifying'].includes(qf.status))
        ?.reportId ?? null
    )
  },

  maxConcurrency: DEFAULT_MAX_RECORD_FILE_UPLOAD_CONCURRENCY,
  setMaxConcurrency: (newValue) => {
    set((state) => ({ ...state, maxConcurrency: newValue }))
  },

  autoStart: true,
  setAutoStart: (newValue) => {
    set(() => ({ autoStart: newValue }))
  },

  addFiles: (files, reportId: string, windowLocation?: string) => {
    const currentReportId = get().getCurrentReportId()
    if (currentReportId && reportId !== currentReportId) {
      return
    }

    const newQueueFiles = files.map(
      (file): QueueFile => ({
        id: `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        file,
        status: 'queue',
        classificationStatus: 'queue',
        reportId,
        windowLocation,
      }),
    )

    set((state) => ({
      queue: [...state.queue, ...newQueueFiles],
    }))
  },
  importPending: (queueFiles) => {
    set((state) => ({ queue: [...state.queue, ...queueFiles] }))
  },
  removeFile: (id) => {
    set((state) => ({ queue: state.queue.filter((qf) => qf.id !== id) }))
  },
  removeFileByRecordFileId: (recordFileId) => {
    set((state) => ({ queue: state.queue.filter((qf) => qf.recordFileId !== recordFileId) }))
  },
  updateFileStatus: (id, status, data) => {
    set((state) => ({
      queue: state.queue.map((qf) => (qf.id === id ? { ...qf, status, ...data } : qf)),
    }))
  },
  updateFileStatusByRecordId: (recordFileId, status, data) => {
    set((state) => ({
      queue: state.queue.map((qf) =>
        qf.recordFileId === recordFileId ? { ...qf, status, ...data } : qf,
      ),
    }))
  },

  clearCompleted: () => {
    set((state) => ({
      queue: state.queue.filter((qf) => qf.status !== 'done'),
    }))
  },

  getFileById: (id) => {
    return get().queue.find((qf) => qf.id === id)
  },
}))
